import React, { useState } from 'react';
import { useSettings } from '../../../contexts/SettingsContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DateTimeTab = () => {
  const { settings, updateSettings } = useSettings();
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState('');
  const [message, setMessage] = useState('');
  const [remainingInfo, setRemainingInfo] = useState(null);

  React.useEffect(() => {
    if (settings?.target_date) {
      const date = new Date(settings.target_date);
      setLocalDate(date.toISOString().split('T')[0]);
      setLocalTime(date.toTimeString().slice(0, 5));
      calculateRemaining(date);
    }
  }, [settings?.target_date]);

  const calculateRemaining = (targetDate) => {
    const now = new Date();
    const diff = Math.floor((targetDate - now) / 1000);
    if (diff > 0) {
      const days = Math.floor(diff / 86400);
      const hours = Math.floor((diff % 86400) / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      setRemainingInfo({ days, hours, minutes, totalSeconds: diff });
    } else {
      setRemainingInfo(null);
    }
  };

  const handleDateChange = (newDate) => {
    setLocalDate(newDate);
    if (newDate && localTime) {
      const dateTime = new Date(`${newDate}T${localTime}`);
      calculateRemaining(dateTime);
    }
  };

  const handleTimeChange = (newTime) => {
    setLocalTime(newTime);
    if (localDate && newTime) {
      const dateTime = new Date(`${localDate}T${newTime}`);
      calculateRemaining(dateTime);
    }
  };

  const handleUpdate = async () => {
    try {
      const dateTime = new Date(`${localDate}T${localTime}`);
      if (dateTime < new Date()) {
        setMessage('⚠️ Target date is in the past!');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      
      await updateSettings({ target_date: dateTime.toISOString() });
      setMessage('✅ Target date updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ Failed to update target date');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleQuickPreset = async (type) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/settings/quick-preset`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const date = new Date(response.data.target_date);
      setLocalDate(date.toISOString().split('T')[0]);
      setLocalTime(date.toTimeString().slice(0, 5));
      calculateRemaining(date);
      setMessage(`✅ Preset applied: ${type.replace('_', ' ')}!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ Failed to apply preset');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (!settings) return <div>Loading...</div>;

  return (
    <div>
      <div className="tab-section">
        <h3 className="section-title">Set Target Date & Time</h3>
        <p className="section-description">
          Manually set your countdown target date and time
        </p>

        <div className="datetime-input-wrapper">
          <div className="form-row">
            <div className="form-group">
              <label>📅 Date</label>
              <input
                type="date"
                value={localDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="datetime-input"
              />
            </div>
            <div className="form-group">
              <label>🕐 Time</label>
              <input
                type="time"
                value={localTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="datetime-input"
              />
            </div>
          </div>

          {remainingInfo && (
            <div className="remaining-preview">
              <div className="preview-label">Countdown Preview:</div>
              <div className="preview-stats">
                <span className="stat-item">
                  <strong>{remainingInfo.days}</strong> days
                </span>
                <span className="stat-separator">•</span>
                <span className="stat-item">
                  <strong>{remainingInfo.hours}</strong> hours
                </span>
                <span className="stat-separator">•</span>
                <span className="stat-item">
                  <strong>{remainingInfo.minutes}</strong> minutes
                </span>
              </div>
              <div className="preview-total">
                Total: <strong>{remainingInfo.totalSeconds.toLocaleString()}</strong> seconds
              </div>
            </div>
          )}

          <button className="btn-primary btn-large" onClick={handleUpdate}>
            🚀 Update Countdown Target
          </button>
        </div>
      </div>

      <div className="tab-section">
        <h3 className="section-title">Quick Presets</h3>
        <p className="section-description">
          Set countdown to common time periods with one click
        </p>

        <div className="preset-grid">
          <button className="preset-btn" onClick={() => handleQuickPreset('1_year')}>
            <div className="preset-icon">📅</div>
            <div className="preset-label">1 Year</div>
            <div className="preset-value">~31.5M seconds</div>
          </button>
          <button className="preset-btn" onClick={() => handleQuickPreset('6_months')}>
            <div className="preset-icon">🗓️</div>
            <div className="preset-label">6 Months</div>
            <div className="preset-value">~15.7M seconds</div>
          </button>
          <button className="preset-btn" onClick={() => handleQuickPreset('100_days')}>
            <div className="preset-icon">📆</div>
            <div className="preset-label">100 Days</div>
            <div className="preset-value">8.64M seconds</div>
          </button>
          <button className="preset-btn" onClick={() => handleQuickPreset('1m_seconds')}>
            <div className="preset-icon">⚡</div>
            <div className="preset-label">1M Seconds</div>
            <div className="preset-value">~11.5 days</div>
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success-message' : 'error-message'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default DateTimeTab;